import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000;

// Construye la URL del WebSocket a partir del backend (http→ws, https→wss).
function buildWsUrl(token) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/api/ws/notifications?token=${encodeURIComponent(token)}`;
}

export function useUnreadNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const closedRef = useRef(false);

  const refresh = async () => {
    try {
      const response = await axios.get(
        `${API}/notifications/inapp?unread_only=true&limit=1`
      );
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.warn('useUnreadNotifications: failed to fetch unread count', error);
    }
  };

  useEffect(() => {
    refresh();
    const intervalId = setInterval(refresh, POLL_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Camino A: conexión WebSocket para notificaciones en tiempo real.
  useEffect(() => {
    const token = localStorage.getItem('onor_token');
    if (!token) return;
    closedRef.current = false;

    const connect = () => {
      if (closedRef.current) return;
      let ws;
      try {
        ws = new WebSocket(buildWsUrl(token));
      } catch (e) {
        return;
      }
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === 'notification') {
            const n = msg.data || {};
            toast(n.title || 'Nueva notificación', { description: n.body });
            refresh();
          }
        } catch (_) {
          /* ignora payloads no-JSON */
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (closedRef.current) return;
        // Reintento con reconexión simple a los 5s.
        reconnectRef.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        try { ws.close(); } catch (_) { /* noop */ }
      };
    };

    connect();

    return () => {
      closedRef.current = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) { /* noop */ }
        wsRef.current = null;
      }
    };
  }, []);

  return { unreadCount, refresh };
}
